// 0-6  (Sun … Sat)
const NAME_TO_NUM = {
  Sunday: 0, Monday: 1, Tuesday: 2, Wednesday: 3,
  Thursday: 4, Friday: 5, Saturday: 6,
};

// get 1-5 occurrence index of that weekday in its month
const nthOfMonth = (m) => Math.floor((m.date() - 1) / 7) + 1;

// helper: nth-to-last occurrence of a weekday in a month
// e.g. second-to-last Saturday → nth=2
function nthToLastOfMonth(d, nth = 1) {
  const clone = d.clone();
  const lastDayOfMonth = clone.clone().endOf("month");

  let count = 0;
  for (
    let m = lastDayOfMonth.clone();
    m.month() === lastDayOfMonth.month();
    m.subtract(1, "day")
  ) {
    if (m.day() === d.day()) {
      count++;
      if (count === nth) {
        return m.date();
      }
    }
  }
  return null;
}

/**
 * Turn ONE textual rule -> predicate(dateMoment) => true/false
 */
export function ruleToMatcher(rule) {
  rule = rule.trim();

  // ------------- “Everyday”
  if (/^every\s*day$/i.test(rule) || /^everyday$/i.test(rule))
    return () => true;

  // ------------- “Weekday”  /  “Monday to Friday”
  if (/weekday/i.test(rule) || /monday\s*to\s*friday/i.test(rule))
    return (d) => d.day() >= 1 && d.day() <= 5;

  // ------------- Simple weekday
  if (NAME_TO_NUM.hasOwnProperty(rule))
    return (d) => d.day() === NAME_TO_NUM[rule];

  // ------------- “Last Friday”
  const lastMatch = /^last\s+(\w+day)$/i.exec(rule);
  if (lastMatch) {
    const wd = NAME_TO_NUM[lastMatch[1]];
    return (d) =>
      d.day() === wd &&
      d.clone().add(7, "days").month() !== d.month(); // next week spills month
  }

  // ------------- “Second-to-last Saturday” (or nth-to-last)
  const specialMonthlyMatch = /^(second|third|fourth|fifth)?-?to-?-?last\s+(\w+day)$/i.exec(rule);
  if (specialMonthlyMatch) {
    const position = specialMonthlyMatch[1]
      ? specialMonthlyMatch[1].toLowerCase()
      : "first";
    const wd = NAME_TO_NUM[specialMonthlyMatch[2]];
    const mapPos = { first: 1, second: 2, third: 3, fourth: 4, fifth: 5 };
    const nth = mapPos[position] || 1;

    return (d) =>
      d.day() === wd && d.date() === nthToLastOfMonth(d, nth);
  }

  // ------------- “Alternate Tuesday”
  const altMatch = /^alternate\s+(\w+day)$/i.exec(rule);
  if (altMatch) {
    const wd = NAME_TO_NUM[altMatch[1]];
    return (d) =>
      d.day() === wd && nthOfMonth(d) % 2 === 1; // 1st,3rd,5th weeks
  }

  // ------------- “Every Two Weeks Monday”
  const every2wMatch = /^every\s+two\s+weeks\s+(\w+day)$/i.exec(rule);
  if (every2wMatch) {
    const wd = NAME_TO_NUM[every2wMatch[1]];
    return (d) => {
      if (d.day() !== wd) return false;
      // anchor: start of year (week 1 Monday) → check if even-numbered week
      const weekNum = Math.floor(d.diff(d.clone().startOf("year"), "weeks"));
      return weekNum % 2 === 0; // every 2 weeks
    };
  }

  // ------------- “4th or 5th Saturday of the Month”
  const ordMatch = /^(\d(st|nd|rd|th))(?:\s+or\s+(\d(st|nd|rd|th)))?\s+(\w+day)/i.exec(
    rule
  );
  if (ordMatch) {
    const wantedOrdinals = [ordMatch[1], ordMatch[3]]
      .filter(Boolean)
      .map((o) => parseInt(o));
    const wd = NAME_TO_NUM[ordMatch[5]];
    return (d) => d.day() === wd && wantedOrdinals.includes(nthOfMonth(d));
  }

// --- "Once a Month (Thursday)" ---
const onceMonthMatch = /^once a month\s*\((\w+day)\)/i.exec(rule);
if (onceMonthMatch) {
  const wd = NAME_TO_NUM[onceMonthMatch[1]];
  return (d) => d.day() === wd; // return ALL Thursdays in that month
}

  // --- "Third or Fourth Sunday of the Month" ---
  const thirdFourthMatch = /^third or fourth (\w+day)/i.exec(rule);
  if (thirdFourthMatch) {
    const wd = NAME_TO_NUM[thirdFourthMatch[1]];
    return (d) => d.day() === wd && [3, 4].includes(nthOfMonth(d));
  }

  // --- "Second to Last Saturday of Every Month" ---
  const secondLastMatch = /^second to last (\w+day)/i.exec(rule);
  if (secondLastMatch) {
    const wd = NAME_TO_NUM[secondLastMatch[1]];
    return (d) => {
      if (d.day() !== wd) return false;
      const next = d.clone().add(7, "days");
      const afterNext = d.clone().add(14, "days");
      return next.month() !== d.month() && afterNext.month() === d.month();
    };
  }

  // --- "Every Second Tuesday of the Month" ---
  const everySecondMatch = /^every second (\w+day) of the month/i.exec(rule);
  if (everySecondMatch) {
    const wd = NAME_TO_NUM[everySecondMatch[1]];
    return (d) => d.day() === wd && nthOfMonth(d) === 2;
  }


  // ------------- “By Appointment”  →  allow nothing automatically
  return () => true;
}
